import type { HttpMethod } from 'openapi-typescript-helpers';

import type { components } from './api.generated';

/**
 * Serializes a query object into a URL query string.
 *
 * @param {Record<string, unknown>} query - The query object to serialize.
 * @returns {string} - The serialized query string.
 */
export function serializeQuery(query: Record<string, unknown>): string {
    const serializedQuery = new URLSearchParams();
    appendQueryParams(serializedQuery, query);
    return serializedQuery.toString();
}

/**
 * Appends query parameters to the URLSearchParams object.
 *
 * @param {URLSearchParams} serializedQuery - The URLSearchParams object to append to.
 * @param {Record<string, unknown>} query - The query object to serialize.
 * @param {string} [prefix] - The prefix for nested query parameters.
 */
function appendQueryParams(
    serializedQuery: URLSearchParams,
    query: Record<string, unknown>,
    prefix: string = '',
): void {
    for (const key in query) {
        const value = query[key as keyof typeof query];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value)) {
            // If the value is an array, append array parameters.
            appendArrayParams(serializedQuery, prefixedKey, value);
        }
        else if (typeof value === 'object' && value !== null) {
            // If the value is an object, recursively append nested query parameters.
            appendQueryParams(
                serializedQuery,
                value as Record<string, unknown>,
                prefixedKey,
            );
        }
        else if (value !== null) {
            // If the value is a primitive, append it directly.
            appendPrimitiveParam(serializedQuery, prefixedKey, value);
        }
    }
}

/**
 * Appends array parameters to the URLSearchParams object.
 *
 * @param {URLSearchParams} serializedQuery - The URLSearchParams object to append to.
 * @param {string} key - The key for the array parameter.
 * @param {unknown[]} array - The array to serialize.
 */
function appendArrayParams(
    serializedQuery: URLSearchParams,
    key: string,
    array: unknown[],
): void {
    array.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
            // If the item is an object, recursively append nested query parameters.
            appendQueryParams(
                serializedQuery,
                item as Record<string, unknown>,
                `${key}[${index}]`,
            );
        }
        else {
            // If the item is a primitive, append it directly.
            serializedQuery.append(`${key}[${index}]`, String(item));
        }
    });
}

/**
 * Appends primitive parameters to the URLSearchParams object.
 *
 * @param {URLSearchParams} serializedQuery - The URLSearchParams object to append to.
 * @param {string} key - The key for the primitive parameter.
 * @param {unknown} value - The primitive value to serialize.
 */
function appendPrimitiveParam(
    serializedQuery: URLSearchParams,
    key: string,
    value: unknown,
): void {
    if (typeof value === 'boolean') {
        // Convert boolean values to 'true' or 'false' strings.
        serializedQuery.append(key, value ? 'true' : 'false');
    }
    else {
        // Convert other primitive values to strings.
        serializedQuery.append(key, String(value));
    }
}

export function toApiHttpMethod(method: HttpMethod): components['schemas']['HttpMethod'] {
    switch (method) {
        case 'get':
            return 'Get';
        case 'post':
            return 'Post';
        case 'put':
            return 'Put';
        case 'delete':
            return 'Delete';
        case 'head':
            return 'Head';
        case 'options':
            return 'Options';
        case 'patch':
            return 'Patch';
        default:
            throw new Error(`Invalid HTTP method: ${method}`);
    }
}
